import React, { useMemo } from "react";
import {
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CommodityPriceVarianceEntry } from "data-handler/ducks/analytics";

type CommodityVariancePoint = {
  commodityIndex: number;
  commodityName: string;
  price: number;
  yValue: number;
  pointType: "price" | "average";
  monthValues?: string[];
  schoolNames?: string[];
  schoolName?: string;
  purchaseDate?: string;
  purchaseCount?: number;
  firstPurchaseDate?: string;
  lastPurchaseDate?: string;
  totalQuantityPurchased?: number;
  totalPricePaid?: number;
  color?: string;
};

type CommodityPriceVarianceChartProps = {
  data: CommodityPriceVarianceEntry[];
  priceLabel?: string;
  averageLabel?: string;
  useSchoolPurchaseDetails?: boolean;
  purchasePriceCurrencyCode?: string;
};

const DOT_COLORS = [
  "#7cb7e6",
  "#f4a259",
  "#5aa469",
  "#f28482",
  "#9a8c98",
  "#84a59d",
  "#f6bd60",
  "#6c9a8b",
  "#b5838d",
  "#8ecae6",
];

const LOG_SCALE_MIN_VALUE = 0.001;
const MIN_LOG_PADDING_DECADES = 0.05;
const MAX_LOG_PADDING_DECADES = 0.25;
const LOG_PADDING_RATIO = 0.12;
const LOG_TICK_MULTIPLIERS = [1, 2, 5];

const toLogScaleSafeValue = (value: number) => {
  if (!Number.isFinite(value)) {
    return value;
  }

  return value > 0 ? value : LOG_SCALE_MIN_VALUE;
};

const formatAxisValue = (value: number) => {
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toLocaleString();
};

const getLogScaleTicks = (minValue: number, maxValue: number): number[] => {
  if (
    !Number.isFinite(minValue) ||
    !Number.isFinite(maxValue) ||
    minValue <= 0 ||
    maxValue <= 0 ||
    maxValue < minValue
  ) {
    return [];
  }

  const minExponent = Math.floor(Math.log10(minValue));
  const maxExponent = Math.ceil(Math.log10(maxValue));
  const ticks: number[] = [];

  for (let exponent = minExponent; exponent <= maxExponent; exponent += 1) {
    const base = Math.pow(10, exponent);
    LOG_TICK_MULTIPLIERS.forEach((multiplier) => {
      const tick = base * multiplier;
      if (tick >= minValue && tick <= maxValue) {
        ticks.push(tick);
      }
    });
  }

  return Array.from(new Set(ticks)).sort((a, b) => a - b);
};

const VarianceTooltip = ({
  active,
  payload,
  priceLabel,
  averageLabel,
  showSchoolPurchaseDetails,
  purchasePriceCurrencyCode,
}: any) => {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload as CommodityVariancePoint;
  if (!point) return null;

  const pointLabel = point.pointType === "average" ? averageLabel : priceLabel;
  const pointPrice = point.price.toLocaleString();
  const pointPriceWithUnit = `${pointPrice}/Kg`;
  const priceWithCurrency =
    point.pointType === "price" && purchasePriceCurrencyCode
      ? `${pointPrice} ${purchasePriceCurrencyCode}`
      : pointPrice;
  const priceWithCurrencyAndUnit =
    point.pointType === "price"
      ? `${priceWithCurrency}/Kg`
      : pointPriceWithUnit;
  const totalPricePaidValue = Number(
    point.totalPricePaid || 0
  ).toLocaleString();
  const totalPricePaidWithCurrency = purchasePriceCurrencyCode
    ? `${totalPricePaidValue} ${purchasePriceCurrencyCode}`
    : totalPricePaidValue;
  const totalQuantityPurchasedValue = Number(
    point.totalQuantityPurchased || 0
  ).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
  const monthValues = (point.monthValues || []).filter(Boolean);
  const purchaseDateMonth = String(point.purchaseDate || "")
    .trim()
    .slice(0, 7);
  const firstPurchaseMonth = String(point.firstPurchaseDate || "")
    .trim()
    .slice(0, 7);
  const lastPurchaseMonth = String(point.lastPurchaseDate || "")
    .trim()
    .slice(0, 7);
  const fallbackMonths = Array.from(
    new Set(
      [purchaseDateMonth, firstPurchaseMonth, lastPurchaseMonth].filter(Boolean)
    )
  ).sort();
  const displayMonthValues = monthValues.length ? monthValues : fallbackMonths;
  const schoolNames = (point.schoolNames || []).filter(Boolean);
  const schoolLabel = showSchoolPurchaseDetails ? "School name" : "Schools";
  const schoolValue = showSchoolPurchaseDetails
    ? point.schoolName
    : schoolNames.join(", ");

  const renderTooltipItem = (
    label: string,
    value: React.ReactNode,
    options?: {
      appendColon?: boolean;
      labelClassName?: string;
    }
  ) => {
    const appendColon = options?.appendColon ?? true;
    const labelClassName = options?.labelClassName;

    return (
      <p className="dashboard-chart-tooltip__item">
        <span
          className={`dashboard-chart-tooltip__item-label${
            labelClassName ? ` ${labelClassName}` : ""
          }`}
        >
          {appendColon ? `${label}:` : label}
        </span>
        <span className="dashboard-chart-tooltip__item-value">{value}</span>
      </p>
    );
  };

  return (
    <div className="dashboard-chart-tooltip">
      {renderTooltipItem(point.commodityName, priceWithCurrencyAndUnit, {
        appendColon: false,
        labelClassName: "dashboard-chart-tooltip__item-label--primary",
      })}
      {point.pointType === "average"
        ? renderTooltipItem(pointLabel, pointPriceWithUnit)
        : null}
      {showSchoolPurchaseDetails &&
      point.pointType === "price" &&
      point.schoolName
        ? renderTooltipItem(schoolLabel, schoolValue)
        : null}
      {!showSchoolPurchaseDetails &&
      point.pointType === "price" &&
      schoolNames.length
        ? renderTooltipItem(schoolLabel, schoolValue)
        : null}
      {!showSchoolPurchaseDetails &&
      point.pointType === "price" &&
      displayMonthValues.length
        ? renderTooltipItem("Months", displayMonthValues.join(", "))
        : null}
      {showSchoolPurchaseDetails &&
      point.pointType === "price" &&
      (point.purchaseCount || 0) > 1 &&
      point.firstPurchaseDate
        ? renderTooltipItem("First purchase date", point.firstPurchaseDate)
        : null}
      {showSchoolPurchaseDetails &&
      point.pointType === "price" &&
      (point.purchaseCount || 0) > 1 &&
      point.lastPurchaseDate
        ? renderTooltipItem("Last purchase date", point.lastPurchaseDate)
        : null}
      {showSchoolPurchaseDetails &&
      point.pointType === "price" &&
      (point.purchaseCount || 0) <= 1 &&
      point.purchaseDate
        ? renderTooltipItem("Purchase date", point.purchaseDate)
        : null}
      {showSchoolPurchaseDetails &&
      point.pointType === "price" &&
      (point.purchaseCount || 0) > 1
        ? renderTooltipItem("Number of purchases", point.purchaseCount)
        : null}
      {point.pointType === "price" &&
      Number.isFinite(point.totalQuantityPurchased)
        ? renderTooltipItem(
            "Total purchase",
            `${totalQuantityPurchasedValue} kg`
          )
        : null}
      {point.pointType === "price" && Number.isFinite(point.totalPricePaid)
        ? renderTooltipItem("Total amount paid", totalPricePaidWithCurrency)
        : null}
    </div>
  );
};

const AverageLineShape = ({ cx, cy, x, y, width, height }: any) => {
  const centerX =
    typeof cx === "number"
      ? cx
      : typeof x === "number" && typeof width === "number"
      ? x + width / 2
      : NaN;
  const centerY =
    typeof cy === "number"
      ? cy
      : typeof y === "number" && typeof height === "number"
      ? y + height / 2
      : NaN;

  if (!Number.isFinite(centerX) || !Number.isFinite(centerY)) {
    return null;
  }

  return (
    <line
      x1={centerX - 10}
      x2={centerX + 10}
      y1={centerY}
      y2={centerY}
      stroke="#111"
      strokeWidth={3}
      strokeLinecap="round"
    />
  );
};

const CommodityPriceVarianceChart: React.FC<CommodityPriceVarianceChartProps> = ({
  data,
  priceLabel = "Price",
  averageLabel = "Average",
  useSchoolPurchaseDetails = false,
  purchasePriceCurrencyCode,
}) => {
  const normalizedData = Array.isArray(data) ? data : [];

  const { pricesData, averageData, commodityNames } = useMemo(() => {
    const prices: CommodityVariancePoint[] = [];
    const averages: CommodityVariancePoint[] = [];
    const sortedCommodityNames = Array.from(
      new Set(
        normalizedData
          .map((entry) => String(entry.commodity_name || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
    const commodityIndexMap = new Map<string, number>(
      sortedCommodityNames.map((commodityName, index) => [commodityName, index])
    );
    const averageIndexMap = new Map<number, boolean>();
    const aggregatedPricePointsMap = new Map<string, CommodityVariancePoint>();
    const schoolPriceAggregateMap = new Map<string, CommodityVariancePoint>();
    const names: string[] = sortedCommodityNames;

    const getCommodityIndex = (commodityName: string) => {
      return commodityIndexMap.get(commodityName);
    };

    normalizedData.forEach((entry) => {
      const commodityName = entry.commodity_name;
      const commodityIndex = getCommodityIndex(commodityName);
      if (commodityIndex === undefined) {
        return;
      }
      const color = DOT_COLORS[commodityIndex % DOT_COLORS.length];
      const averagePrice = Number(entry.average_price);
      const paidPrice = Number(entry.price_per_kg);
      const quantityPurchased = Number(
        entry.purchased_total_quantity ??
          entry.purchase_quantity ??
          entry.quantity_purchased
      );
      const amountPaid = Number(entry.total_amount_paid);
      const quantityValue = Number.isFinite(quantityPurchased)
        ? quantityPurchased
        : undefined;
      const amountPaidValue = Number.isFinite(amountPaid)
        ? amountPaid
        : undefined;
      const schoolNamesFromEntry = Array.from(
        new Set(
          Array.isArray(entry.schools)
            ? entry.schools
                .map((schoolEntry) =>
                  String(schoolEntry?.school_name || "").trim()
                )
                .filter(Boolean)
            : []
        )
      );

      if (useSchoolPurchaseDetails) {
        if (Number.isFinite(paidPrice)) {
          const pointKey = `${commodityIndex}|${paidPrice}`;
          const purchaseDate = entry.purchase_date;
          const existingPoint = schoolPriceAggregateMap.get(pointKey);

          if (!existingPoint) {
            schoolPriceAggregateMap.set(pointKey, {
              commodityIndex,
              commodityName,
              price: paidPrice,
              yValue: toLogScaleSafeValue(paidPrice),
              pointType: "price",
              schoolName: entry.school_name,
              purchaseDate,
              purchaseCount: 1,
              firstPurchaseDate: purchaseDate,
              lastPurchaseDate: purchaseDate,
              totalQuantityPurchased: quantityValue,
              totalPricePaid:
                quantityValue === undefined
                  ? undefined
                  : quantityValue * paidPrice,
              color,
            });
          } else {
            const nextCount = (existingPoint.purchaseCount || 0) + 1;
            const firstDate = existingPoint.firstPurchaseDate;
            const lastDate = existingPoint.lastPurchaseDate;
            const existingTotalQty = existingPoint.totalQuantityPurchased;
            const nextTotalQty =
              quantityValue === undefined
                ? existingTotalQty
                : (existingTotalQty || 0) + quantityValue;
            const nextFirstDate =
              purchaseDate && (!firstDate || purchaseDate < firstDate)
                ? purchaseDate
                : firstDate;
            const nextLastDate =
              purchaseDate && (!lastDate || purchaseDate > lastDate)
                ? purchaseDate
                : lastDate;

            schoolPriceAggregateMap.set(pointKey, {
              ...existingPoint,
              schoolName: existingPoint.schoolName || entry.school_name,
              purchaseCount: nextCount,
              firstPurchaseDate: nextFirstDate,
              lastPurchaseDate: nextLastDate,
              totalQuantityPurchased: nextTotalQty,
              totalPricePaid:
                quantityValue === undefined
                  ? existingPoint.totalPricePaid
                  : (existingPoint.totalPricePaid || 0) +
                    quantityValue * paidPrice,
            });
          }
        }
      } else {
        (entry.distinct_prices || []).forEach((price) => {
          const numericPrice = Number(price);
          const monthValue = String(
            entry.year_month || entry.month || ""
          ).trim();
          if (!Number.isFinite(numericPrice)) {
            return;
          }

          const pointKey = `${commodityIndex}|${numericPrice}`;
          const existingPoint = aggregatedPricePointsMap.get(pointKey);

          if (existingPoint) {
            const existingMonthValues = existingPoint.monthValues || [];
            const nextMonthValues = monthValue
              ? Array.from(new Set([...existingMonthValues, monthValue])).sort()
              : existingMonthValues;
            const existingTotalQty = existingPoint.totalQuantityPurchased;
            const nextTotalQty =
              quantityValue === undefined
                ? existingTotalQty
                : (existingTotalQty || 0) + quantityValue;
            const existingTotalAmount = existingPoint.totalPricePaid;
            const nextTotalAmount =
              amountPaidValue === undefined
                ? existingTotalAmount
                : (existingTotalAmount || 0) + amountPaidValue;
            const existingSchoolNames = existingPoint.schoolNames || [];
            const nextSchoolNames = Array.from(
              new Set([...existingSchoolNames, ...schoolNamesFromEntry])
            );

            aggregatedPricePointsMap.set(pointKey, {
              ...existingPoint,
              monthValues: nextMonthValues,
              schoolNames: nextSchoolNames,
              totalQuantityPurchased: nextTotalQty,
              totalPricePaid: nextTotalAmount,
            });
            return;
          }

          aggregatedPricePointsMap.set(pointKey, {
            commodityIndex,
            commodityName,
            price: numericPrice,
            yValue: toLogScaleSafeValue(numericPrice),
            pointType: "price",
            monthValues: monthValue ? [monthValue] : [],
            schoolNames: schoolNamesFromEntry,
            totalQuantityPurchased: quantityValue,
            totalPricePaid: amountPaidValue,
            color,
          });
        });
      }

      if (
        !Number.isFinite(averagePrice) ||
        averageIndexMap.has(commodityIndex)
      ) {
        return;
      }

      averageIndexMap.set(commodityIndex, true);

      averages.push({
        commodityIndex,
        commodityName,
        price: averagePrice,
        yValue: toLogScaleSafeValue(averagePrice),
        pointType: "average",
      });
    });

    if (useSchoolPurchaseDetails) {
      prices.push(...schoolPriceAggregateMap.values());
    } else {
      prices.push(...aggregatedPricePointsMap.values());
    }

    return { pricesData: prices, averageData: averages, commodityNames: names };
  }, [normalizedData, useSchoolPurchaseDetails]);

  const logScaleDomain = useMemo(() => {
    const allValues = [...pricesData, ...averageData]
      .map((point) => Number(point.price))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!allValues.length) {
      return [LOG_SCALE_MIN_VALUE, 1];
    }

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const logRange = Math.log10(maxValue) - Math.log10(minValue);
    const paddingDecades = Math.max(
      MIN_LOG_PADDING_DECADES,
      Math.min(MAX_LOG_PADDING_DECADES, logRange * LOG_PADDING_RATIO)
    );
    const paddingFactor = Math.pow(10, paddingDecades);
    const paddedMin = Math.max(LOG_SCALE_MIN_VALUE, minValue / paddingFactor);
    const paddedMax = maxValue * paddingFactor;

    if (paddedMax <= paddedMin) {
      return [paddedMin, paddedMin * 1.5];
    }

    return [paddedMin, paddedMax];
  }, [averageData, pricesData]);

  const logScaleTicks = useMemo(() => {
    return getLogScaleTicks(logScaleDomain[0], logScaleDomain[1]);
  }, [logScaleDomain]);

  return (
    <div className="dashboard-commodity-variance-chart">
      <div className="dashboard-commodity-variance-chart__plot">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 8, bottom: 52, left: 12 }}>
            <CartesianGrid stroke="#f0f3f5" vertical={false} />
            <XAxis
              dataKey="commodityIndex"
              type="number"
              domain={
                commodityNames.length
                  ? [0, Math.max(commodityNames.length - 1, 0)]
                  : [0, 0]
              }
              allowDecimals={false}
              tickCount={Math.max(commodityNames.length, 1)}
              tickFormatter={(value: number) => {
                return commodityNames[value] || "";
              }}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={65}
            />
            <YAxis
              dataKey="yValue"
              type="number"
              scale="log"
              domain={logScaleDomain}
              ticks={logScaleTicks}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={56}
              tickMargin={6}
              tickFormatter={formatAxisValue}
            />
            <Tooltip
              content={
                <VarianceTooltip
                  priceLabel={priceLabel}
                  averageLabel={averageLabel}
                  showSchoolPurchaseDetails={useSchoolPurchaseDetails}
                  purchasePriceCurrencyCode={purchasePriceCurrencyCode}
                />
              }
            />

            <Scatter name={priceLabel} data={pricesData}>
              {pricesData.map((entry, index) => (
                <Cell
                  key={`${entry.commodityName}-${entry.price}-${index}`}
                  fill={entry.color}
                />
              ))}
            </Scatter>

            <Scatter
              name={averageLabel}
              data={averageData}
              fill="#111"
              shape={<AverageLineShape />}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CommodityPriceVarianceChart;
